(() => {
  const KEY = '__JRMGroupedVariantPickerV1';

  if (window[KEY]?.scan) {
    window[KEY].scan();
    return;
  }

  const TYPE_LABELS = {
    cabinet_knob: 'Cabinet Knobs',
    cup_pull: 'Cup Pulls',
    folding_pull: 'Folding Pulls',
    bar_pull: 'Bar Pulls',
    t_bar_pull: 'T-Bar Pulls'
  };

  const normalizeId = (value) => {
    if (value == null) return '';
    return String(value).split('/').pop();
  };

  const gcd = (a, b) => {
    let x = Math.abs(a);
    let y = Math.abs(b);
    while (y) {
      const next = x % y;
      x = y;
      y = next;
    }
    return x || 1;
  };

  const formatInches = (millimeters) => {
    const mm = Number(millimeters);
    if (!Number.isFinite(mm) || mm <= 0) return '';

    const totalSixteenths = Math.max(1, Math.round((mm / 25.4) * 16));
    const whole = Math.floor(totalSixteenths / 16);
    let numerator = totalSixteenths % 16;

    if (!numerator) return `${whole} in`;

    const divisor = gcd(numerator, 16);
    numerator /= divisor;
    const denominator = 16 / divisor;

    return whole
      ? `${whole} ${numerator}/${denominator} in`
      : `${numerator}/${denominator} in`;
  };

  const formatMillimeters = (millimeters) => {
    const mm = Number(millimeters);
    if (!Number.isFinite(mm)) return '';
    const rounded = Math.round(mm * 100) / 100;
    return Number.isInteger(rounded) ? String(rounded) : String(rounded);
  };

  const formatSize = (variant) => {
    const label = String(variant?.sizeLabel || '').trim();
    return label || String(variant?.nativeValue || '');
  };

  const cleanStyleName = (style) => {
    const displayName = String(style?.displayName || '').trim();
    if (!displayName) return String(style?.styleCode || '').trim();

    const dashIndex = displayName.indexOf('—');
    if (dashIndex >= 0) {
      return displayName.slice(dashIndex + 1).trim();
    }

    return displayName
      .replace(/^Style\s*#?\d+\s*[-–—:]?\s*/i, '')
      .trim();
  };

  const createButton = (className, text) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = className;
    button.textContent = text;
    return button;
  };

  const initRoot = (root) => {
    if (!root || root.dataset.jrmGvpInitialized === 'true') return;

    const dataElement = document.getElementById(root.dataset.jrmDataId || '');
    const nativePicker = document.getElementById(root.dataset.nativePickerId || '');

    if (!dataElement || !nativePicker) return;

    let data;
    try {
      data = JSON.parse(dataElement.textContent);
    } catch (error) {
      console.warn('JRM grouped variant picker: invalid JSON', error);
      return;
    }

    const styles = Array.isArray(data.styles) ? data.styles : [];
    const variants = Array.isArray(data.variants) ? data.variants : [];

    if (!styles.length || !variants.length) return;

    const styleByKey = new Map();
    styles.forEach((style) => {
      if (style?.familyKey) styleByKey.set(String(style.familyKey), style);
    });

    const variantById = new Map();
    const variantsByFamily = new Map();

    variants.forEach((variant) => {
      const id = normalizeId(variant?.id);
      const familyKey = String(variant?.familyKey || '');
      if (!id || !familyKey) return;

      variantById.set(id, variant);
      if (!variantsByFamily.has(familyKey)) variantsByFamily.set(familyKey, []);
      variantsByFamily.get(familyKey).push(variant);
    });

    const nativeInputs = Array.from(nativePicker.querySelectorAll('input[is-value]'));
    const nativeValues = new Set(nativeInputs.map((input) => String(input.value)));

    const mappingComplete =
      variantById.size === variants.length &&
      styles.every((style) => style?.familyKey && style?.handleType) &&
      variants.every((variant) =>
        styleByKey.has(String(variant?.familyKey || '')) &&
        nativeValues.has(String(variant?.nativeValue || ''))
      );

    if (!mappingComplete) {
      console.warn('JRM grouped variant picker: mapping validation failed; native picker retained');
      return;
    }

    const typeOrder = [];
    styles.forEach((style) => {
      const type = String(style.handleType || '');
      if (type && !typeOrder.includes(type)) typeOrder.push(type);
    });

    if (!typeOrder.length) return;

    const typeRoot = root.querySelector('[data-jrm-gvp-types]');
    const styleRoot = root.querySelector('[data-jrm-gvp-styles]');
    const sizeSection = root.querySelector('[data-jrm-gvp-size-section]');
    const sizeRoot = root.querySelector('[data-jrm-gvp-sizes]');
    const selectedStyleLabel = root.querySelector('[data-jrm-gvp-selected-style]');
    const selectedSizeLabel = root.querySelector('[data-jrm-gvp-selected-size]');
    const status = root.querySelector('[data-jrm-gvp-status]');

    if (!typeRoot || !styleRoot || !sizeSection || !sizeRoot) return;

    window.JRMGroupedVariantPickerControllers =
      window.JRMGroupedVariantPickerControllers || {};

    const controllerKey = root.id;
    window.JRMGroupedVariantPickerControllers[controllerKey]?.abort?.();

    const controller = new AbortController();
    window.JRMGroupedVariantPickerControllers[controllerKey] = controller;

    const state = {
      viewType: typeOrder[0],
      selectedVariant: null,
      pendingTimer: null,
      active: true
    };

    const clearPending = () => {
      if (state.pendingTimer) {
        window.clearTimeout(state.pendingTimer);
        state.pendingTimer = null;
      }
      root.removeAttribute('aria-busy');
    };

    const failOpen = (reason) => {
      if (!state.active) return;
      state.active = false;
      clearPending();
      root.hidden = true;
      nativePicker.classList.remove('jrm-gvp-native-hidden');
      nativePicker.removeAttribute('aria-hidden');
      console.warn(`JRM grouped variant picker: reverted to native picker (${reason})`);
    };

    const getAvailableVariantsForStyle = (familyKey) =>
      (variantsByFamily.get(String(familyKey)) || []).filter((variant) => variant.available !== false);

    const getFirstAvailableVariantForType = (type) => {
      const typeStyles = styles.filter((style) => String(style.handleType) === String(type));
      for (const style of typeStyles) {
        const available = getAvailableVariantsForStyle(style.familyKey);
        if (available.length) return available[0];
      }
      return null;
    };

    const setStatus = (variant) => {
      if (!status || !variant) return;
      const style = styleByKey.get(String(variant.familyKey));
      const styleName = cleanStyleName(style);
      const size = formatSize(variant);
      status.textContent = size ? `Selected ${styleName}, ${size}` : `Selected ${styleName}`;
    };

    const selectNativeVariant = (variant) => {
      if (!state.active || !variant) return;

      const input = Array.from(nativePicker.querySelectorAll('input[is-value]'))
        .find((candidate) => String(candidate.value) === String(variant.nativeValue));

      if (!input) {
        failOpen(`native value not found: ${variant.nativeValue}`);
        return;
      }

      root.setAttribute('aria-busy', 'true');
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));

      clearPending();
      root.setAttribute('aria-busy', 'true');
      state.pendingTimer = window.setTimeout(() => {
        failOpen('variant change timeout');
      }, 8000);
    };

    const renderTypes = () => {
      typeRoot.replaceChildren();

      typeOrder.forEach((type) => {
        const button = createButton(
          'jrm-gvp__choice jrm-gvp__type',
          TYPE_LABELS[type] || type.replaceAll('_', ' ')
        );
        const active = type === state.viewType;
        button.dataset.jrmGvpType = type;
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
        if (active) button.classList.add('is-active');

        button.addEventListener('click', () => {
          if (!state.active || type === state.viewType) return;
          state.viewType = type;
          renderAll();

          const firstVariant = getFirstAvailableVariantForType(type);
          if (firstVariant) selectNativeVariant(firstVariant);
        }, { signal: controller.signal });

        typeRoot.append(button);
      });
    };

    const renderStyles = () => {
      styleRoot.replaceChildren();

      const visibleStyles = styles.filter(
        (style) => String(style.handleType) === String(state.viewType)
      );

      const selectedFamily =
        state.selectedVariant && String(state.selectedVariant.handleType) === String(state.viewType)
          ? String(state.selectedVariant.familyKey)
          : '';

      visibleStyles.forEach((style) => {
        const familyKey = String(style.familyKey);
        const availableVariants = getAvailableVariantsForStyle(familyKey);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'jrm-gvp__style';
        button.dataset.jrmGvpFamily = familyKey;
        button.disabled = availableVariants.length === 0;

        const active = selectedFamily === familyKey;
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
        if (active) button.classList.add('is-active');

        const media = document.createElement('span');
        media.className = 'jrm-gvp__style-media';

        if (style.thumbnail) {
          const image = document.createElement('img');
          image.src = style.thumbnail;
          if (style.thumbnailSrcset) image.srcset = String(style.thumbnailSrcset);
          image.sizes = String(style.thumbnailSizes || '120px');
          image.alt = cleanStyleName(style);
          image.loading = 'lazy';
          image.decoding = 'async';
          media.append(image);
        }

        const copy = document.createElement('span');
        copy.className = 'jrm-gvp__style-copy';

        const code = document.createElement('span');
        code.className = 'jrm-gvp__style-code';
        code.textContent = String(style.styleCode || '');

        const name = document.createElement('span');
        name.className = 'jrm-gvp__style-name';
        name.textContent = cleanStyleName(style);

        copy.append(code, name);
        button.append(media, copy);

        button.addEventListener('click', () => {
          if (!state.active || !availableVariants.length) return;
          selectNativeVariant(availableVariants[0]);
        }, { signal: controller.signal });

        styleRoot.append(button);
      });

      const selectedStyle = selectedFamily ? styleByKey.get(selectedFamily) : null;
      if (selectedStyleLabel) {
        selectedStyleLabel.textContent = selectedStyle ? cleanStyleName(selectedStyle) : '';
      }
    };

    const renderSizes = () => {
      sizeRoot.replaceChildren();

      const selected = state.selectedVariant;
      const selectionVisible =
        selected && String(selected.handleType) === String(state.viewType);

      if (!selectionVisible) {
        sizeSection.hidden = true;
        if (selectedSizeLabel) selectedSizeLabel.textContent = '';
        return;
      }

      const familyVariants = variantsByFamily.get(String(selected.familyKey)) || [];
      if (familyVariants.length <= 1) {
        sizeSection.hidden = true;
        if (selectedSizeLabel) selectedSizeLabel.textContent = '';
        return;
      }

      sizeSection.hidden = false;

      familyVariants.forEach((variant) => {
        const label = formatSize(variant);
        const button = createButton('jrm-gvp__choice jrm-gvp__size', label);
        const active = normalizeId(variant.id) === normalizeId(selected.id);
        button.dataset.jrmGvpVariantId = normalizeId(variant.id);
        button.disabled = variant.available === false;
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
        if (active) button.classList.add('is-active');
        if (variant.available === false) button.setAttribute('aria-label', `${label} — Sold out`);

        button.addEventListener('click', () => {
          if (!state.active || variant.available === false || active) return;
          selectNativeVariant(variant);
        }, { signal: controller.signal });

        sizeRoot.append(button);
      });

      if (selectedSizeLabel) selectedSizeLabel.textContent = formatSize(selected);
    };

    const renderAll = () => {
      renderTypes();
      renderStyles();
      renderSizes();
    };

    const syncVariant = (variant) => {
      if (!variant || !state.active) return;
      state.selectedVariant = variant;
      state.viewType = String(variant.handleType || state.viewType);
      clearPending();
      renderAll();
      setStatus(variant);
    };

    document.addEventListener('variant:change', (event) => {
      const eventVariant = event.detail?.variant;
      const mapped = variantById.get(normalizeId(eventVariant?.id));
      if (!mapped) return;
      syncVariant(mapped);
    }, { signal: controller.signal });

    const initialVariant = variantById.get(normalizeId(data.initialVariantId));
    if (initialVariant) {
      state.selectedVariant = initialVariant;
      state.viewType = String(initialVariant.handleType || state.viewType);
    }

    renderAll();
    if (initialVariant) setStatus(initialVariant);

    nativePicker.parentNode?.insertBefore(root, nativePicker);
    nativePicker.classList.add('jrm-gvp-native-hidden');
    nativePicker.setAttribute('aria-hidden', 'true');
    root.hidden = false;
    root.dataset.jrmGvpInitialized = 'true';
  };

  const scan = () => {
    const roots = document.querySelectorAll('[data-jrm-grouped-variant-picker]');
    if (!roots.length) return;

    const run = () => roots.forEach(initRoot);

    if (window.customElements?.whenDefined) {
      customElements.whenDefined('hdt-variant-picker').then(run).catch(run);
    } else {
      run();
    }
  };

  window[KEY] = { scan };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan, { once: true });
  } else {
    scan();
  }

  document.addEventListener('shopify:section:load', scan);
})();

(() => {
  const KEY = '__JRMLandscapeGalleryDotsV1';
  if (window[KEY]?.scan) {
    window[KEY].scan();
    return;
  }

  const QUERY = '(max-width: 1149px) and (orientation: landscape) and (hover: none) and (pointer: coarse)';
  const MODE_CLASS = 'jrm-landscape-dots-mode';
  const MORE = 'View more product photos';
  const LESS = 'Show fewer product photos';
  const STYLE_ID = 'jrm-landscape-gallery-dots-style';
  const mql = window.matchMedia(QUERY);

  const ensureStyle = () => {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .jrm-gallery-dots { display: none; }
      @media screen and (max-width: 1149px) and (orientation: landscape) and (hover: none) and (pointer: coarse) {
        .hdt-product-media-wrapps hdt-product-media.hdt-product-media.${MODE_CLASS} .hdt-product-media__thumb {
          display: none !important;
          flex: 0 0 0 !important;
          width: 0 !important;
          height: 0 !important;
          min-height: 0 !important;
          max-height: 0 !important;
          margin: 0 !important;
          padding: 0 !important;
          overflow: hidden !important;
        }
        hdt-product-media.${MODE_CLASS}.jrm-gallery-controls-host {
          padding-bottom: 0 !important;
        }
        hdt-product-media.${MODE_CLASS} > .jrm-gallery-view-controls {
          display: none !important;
        }
        hdt-product-media.${MODE_CLASS} .hdt-product__media-counter {
          display: none !important;
        }
        hdt-product-media.${MODE_CLASS} > .jrm-gallery-dots:not([hidden]) {
          display: flex !important;
          width: 100%;
          box-sizing: border-box;
          align-items: center;
          justify-content: center;
          gap: 2px;
          overflow-x: auto;
          overscroll-behavior-inline: contain;
          scrollbar-width: none;
        }
        hdt-product-media.${MODE_CLASS} > .jrm-gallery-dots::-webkit-scrollbar {
          display: none;
        }
        .jrm-gallery-dot {
          appearance: none;
          flex: 0 0 24px;
          width: 24px;
          height: 24px;
          padding: 0;
          border: 0;
          background: transparent;
          color: currentColor;
          display: grid;
          place-items: center;
          cursor: pointer;
        }
        .jrm-gallery-dot::before {
          content: '';
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: currentColor;
          opacity: .28;
          transition: opacity .15s ease, transform .15s ease;
        }
        .jrm-gallery-dot[aria-current='true']::before {
          opacity: .82;
          transform: scale(1.2);
        }
        .jrm-gallery-dot:focus-visible {
          outline: 1px solid currentColor;
          outline-offset: -2px;
          border-radius: 50%;
        }
      }
      @media screen and (min-width: 768px) and (max-width: 1149px) and (orientation: landscape) and (hover: none) and (pointer: coarse) {
        hdt-product-media.${MODE_CLASS} > .jrm-gallery-dots:not([hidden]) {
          flex: 0 0 5.6rem !important;
          height: 5.6rem !important;
          min-height: 5.6rem !important;
          max-height: 5.6rem !important;
        }
      }
      @media screen and (max-width: 767px) and (orientation: landscape) and (hover: none) and (pointer: coarse) {
        hdt-product-media.${MODE_CLASS} > .jrm-gallery-dots:not([hidden]) {
          min-height: 32px;
          padding-block: 4px;
        }
      }
    `;
    document.head.append(style);
  };

  const initMedia = (media) => {
    if (!media || media.dataset.jrmLandscapeDotsInitialized === 'true') return;

    const mainRoot = media.querySelector('.hdt-product-media__main');
    if (!mainRoot) return;

    ensureStyle();
    media.dataset.jrmLandscapeDotsInitialized = 'true';

    const dots = document.createElement('div');
    dots.className = 'jrm-gallery-dots';
    dots.hidden = true;
    dots.setAttribute('role', 'group');
    dots.setAttribute('aria-label', 'Product image navigation');

    const thumb = media.querySelector('.hdt-product-media__thumb');
    if (thumb) thumb.before(dots);
    else mainRoot.after(dots);

    let autoExpanded = false;
    let frame = 0;

    const mains = () => Array.from(mainRoot.querySelectorAll('[data-media-id]'));
    const visibleMains = () => mains().filter((item) => !item.hidden && item.getClientRects().length);
    const controlWrap = () => media.querySelector('.jrm-gallery-view-controls');
    const controlButton = () => media.querySelector('.jrm-gallery-view-controls__button');

    const activeIndex = (items) => {
      if (!items.length) return 0;
      const viewport = mainRoot.querySelector('.hdt-slider__viewport') || mainRoot;
      const vr = viewport.getBoundingClientRect();
      const vx = vr.left + vr.width / 2;
      const vy = vr.top + vr.height / 2;
      let best = 0;
      let bestScore = Number.POSITIVE_INFINITY;

      items.forEach((item, index) => {
        const rect = item.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        const x = rect.left + rect.width / 2;
        const y = rect.top + rect.height / 2;
        const score = Math.abs(x - vx) + Math.abs(y - vy);
        if (score < bestScore) {
          bestScore = score;
          best = index;
        }
      });
      return best;
    };

    const syncDots = () => {
      if (!mql.matches || dots.hidden) return;
      const items = visibleMains();
      const index = activeIndex(items);
      dots.querySelectorAll('.jrm-gallery-dot').forEach((dot, dotIndex) => {
        dot.setAttribute('aria-current', dotIndex === index ? 'true' : 'false');
      });
    };

    const renderDots = () => {
      const items = visibleMains();
      const show = mql.matches && items.length > 1;
      dots.hidden = !show;
      dots.replaceChildren();
      if (!show) return;

      const selected = activeIndex(items);
      items.forEach((item, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'jrm-gallery-dot';
        dot.setAttribute('aria-label', `View image ${index + 1} of ${items.length}`);
        dot.setAttribute('aria-current', index === selected ? 'true' : 'false');
        dot.addEventListener('click', () => {
          media.sliderMain?.goToTarget?.(item);
          dot.setAttribute('aria-current', 'true');
          dots.querySelectorAll('.jrm-gallery-dot').forEach((other) => {
            if (other !== dot) other.setAttribute('aria-current', 'false');
          });
          window.setTimeout(syncDots, 80);
          window.setTimeout(syncDots, 260);
        });
        dots.append(dot);
      });
    };

    const scheduleDots = (rebuild = false) => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        frame = 0;
        if (rebuild) renderDots();
        else syncDots();
      });
    };

    const applyMode = () => {
      const on = mql.matches;
      media.classList.toggle(MODE_CLASS, on);

      const wrap = controlWrap();
      const button = controlButton();

      if (on) {
        if (!autoExpanded && wrap && !wrap.hidden && button && button.textContent.trim() === MORE) {
          autoExpanded = true;
          button.click();
        }
        scheduleDots(true);
        window.setTimeout(() => scheduleDots(true), 120);
      } else {
        dots.hidden = true;
        dots.replaceChildren();
        if (autoExpanded && button && button.textContent.trim() === LESS) {
          button.click();
        }
        autoExpanded = false;
      }
    };

    const observer = new MutationObserver((mutations) => {
      if (!mql.matches) return;
      const rebuild = mutations.some((mutation) =>
        mutation.type === 'childList' || mutation.attributeName === 'hidden'
      );
      scheduleDots(rebuild);
    });
    observer.observe(mainRoot, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['hidden', 'class', 'style', 'aria-current', 'aria-selected', 'selected']
    });

    const delayedSync = () => {
      scheduleDots(false);
      window.setTimeout(syncDots, 80);
      window.setTimeout(syncDots, 260);
    };

    mainRoot.addEventListener('pointerup', delayedSync, { passive: true });
    mainRoot.addEventListener('touchend', delayedSync, { passive: true });
    mainRoot.addEventListener('transitionend', delayedSync, true);
    mainRoot.addEventListener('click', delayedSync, true);

    document.addEventListener('variant:change', () => {
      window.setTimeout(applyMode, 0);
      window.setTimeout(applyMode, 140);
    });

    window.addEventListener('resize', () => window.setTimeout(applyMode, 60), { passive: true });
    mql.addEventListener?.('change', applyMode);

    applyMode();
    window.setTimeout(applyMode, 250);
    window.setTimeout(applyMode, 900);
  };

  const scan = () => {
    const run = () => document.querySelectorAll('hdt-product-media').forEach(initMedia);
    if (window.customElements?.whenDefined) {
      customElements.whenDefined('hdt-product-media').then(run).catch(run);
    } else {
      run();
    }
  };

  window[KEY] = { scan };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan, { once: true });
  } else {
    scan();
  }
  document.addEventListener('shopify:section:load', scan);
})();
